import type {
  ChatCompletion,
  ChatCompletionChunk,
} from 'openai/resources/chat/completions';
import type { CompletionUsage } from 'openai/resources/completions';
import type { Stream } from 'openai/streaming';
import { ModelBehaviorError } from '../../errors';
import * as protocol from '../../types/protocol';
import { FAKE_ID } from './chat-completions-model';
import { OPENAI_CHAT_COMPLETIONS_RAW_MODEL_EVENT_SOURCE } from './raw-model-events';

type ActiveOutputPart =
  | {
      type: 'output_text';
      partId: string;
    }
  | {
      type: 'reasoning';
      partId: string;
    }
  | {
      type: 'function_call';
      partId: string;
      functionCallIndex: number;
    };

type StreamingState = {
  started: boolean;
  text_content: protocol.OutputText | null;
  refusal_content: protocol.Refusal | null;
  function_calls: Record<number, protocol.FunctionCallItem>;
  functionCallPartIds: Record<number, string>;
  closedFunctionCallIndexes: Set<number>;
  reasoning: string;
  reasoningStartedAtMs: number | undefined;
  reasoningLastAtMs: number | undefined;
  finishReason: ChatCompletion['choices'][number]['finish_reason'] | null;
  activePart: ActiveOutputPart | null;
  nextPartIndex: number;
};

export async function* convertChatCompletionsStreamToResponses(
  response: ChatCompletion,
  stream: Stream<ChatCompletionChunk>,
): AsyncIterable<protocol.StreamEvent> {
  let usage: CompletionUsage | undefined;
  const state: StreamingState = {
    started: false,
    text_content: null,
    refusal_content: null,
    function_calls: {},
    functionCallPartIds: {},
    closedFunctionCallIndexes: new Set(),
    reasoning: '',
    reasoningStartedAtMs: undefined,
    reasoningLastAtMs: undefined,
    finishReason: null,
    activePart: null,
    nextPartIndex: 0,
  };
  let lastChunk: ChatCompletionChunk | undefined;

  for await (const chunk of stream) {
    lastChunk = chunk;
    if (chunk.id && (response.id === FAKE_ID || !response.id)) {
      response.id = chunk.id;
    }

    if (!state.started) {
      state.started = true;
      yield {
        type: 'response_started',
        providerData: {
          ...chunk,
        },
      };
    }

    // always yield the raw event
    yield {
      type: 'model',
      event: chunk,
      providerData: {
        rawModelEventSource: OPENAI_CHAT_COMPLETIONS_RAW_MODEL_EVENT_SOURCE,
      },
    };

    // This is always set by the OpenAI API, but not by others e.g. LiteLLM
    usage = (chunk as any).usage || undefined;

    const primaryChoice = chunk.choices?.[0];
    if (!primaryChoice) continue;
    if (primaryChoice.finish_reason) {
      state.finishReason = primaryChoice.finish_reason;
    }
    if (!primaryChoice.delta) continue;
    const delta = primaryChoice.delta;
    const textDelta =
      typeof delta.content === 'string' && delta.content.length > 0
        ? delta.content
        : undefined;
    const reasoningDelta = getReasoningDelta(delta);
    const toolCallDeltas = Array.isArray(delta.tool_calls)
      ? delta.tool_calls
      : [];
    const hasToolCallOutput = toolCallDeltas.some((toolCallDelta) =>
      hasMeaningfulToolCallDelta(state, toolCallDelta),
    );

    const semanticPartOutputCount = [
      textDelta !== undefined,
      reasoningDelta !== undefined,
      hasToolCallOutput,
    ].filter(Boolean).length;
    if (semanticPartOutputCount > 1) {
      throw new ModelBehaviorError(
        'OpenAI Chat Completions stream chunk contained multiple output part types.',
      );
    }

    // Handle text
    if (textDelta) {
      if (!state.text_content) {
        state.text_content = {
          text: '',
          type: 'output_text',
          providerData: { annotations: [] },
        };
      }
      for (const event of openScalarPart(
        state,
        response,
        'output_text',
        chunk,
      )) {
        yield event;
      }
      yield {
        type: 'output_text_delta',
        delta: textDelta,
        providerData: {
          ...chunk,
        },
      };
      state.text_content.text += textDelta;
    }

    if (reasoningDelta) {
      for (const event of openScalarPart(state, response, 'reasoning', chunk)) {
        yield event;
      }
      const now = Date.now();
      state.reasoningStartedAtMs ??= now;
      state.reasoningLastAtMs = now;
      state.reasoning += reasoningDelta;
      yield {
        type: 'reasoning_delta',
        delta: reasoningDelta,
        providerData: {
          ...chunk,
        },
      };
    }

    // Handle refusals
    if ('refusal' in delta && delta.refusal) {
      if (!state.refusal_content) {
        state.refusal_content = { refusal: '', type: 'refusal' };
      }
      state.refusal_content.refusal += delta.refusal;
    }

    // Handle tool calls
    if (toolCallDeltas.length > 0) {
      for (const event of applyToolCallDeltas(
        state,
        response,
        toolCallDeltas,
        chunk,
      )) {
        yield event;
      }
    }
  }

  for (const event of closeActivePart(state, lastChunk)) {
    yield event;
  }

  // Final output message
  const outputs: protocol.OutputModelItem[] = [];
  const outputItemId = response.id || FAKE_ID;

  if (state.reasoning) {
    const durationMs =
      state.reasoningStartedAtMs !== undefined &&
      state.reasoningLastAtMs !== undefined
        ? Math.max(0, state.reasoningLastAtMs - state.reasoningStartedAtMs)
        : undefined;
    outputs.push({
      type: 'reasoning',
      content: [],
      rawContent: [
        {
          type: 'reasoning_text',
          text: state.reasoning,
          ...(durationMs !== undefined
            ? { providerData: { duration_ms: durationMs } }
            : {}),
        },
      ],
    });
  }

  if (state.text_content || state.refusal_content) {
    const content: protocol.AssistantContent[] = [];
    if (state.text_content) {
      content.push(state.text_content);
    }
    if (state.refusal_content) {
      content.push(state.refusal_content);
    }
    outputs.push({
      id: outputItemId,
      content,
      role: 'assistant',
      type: 'message',
      status: 'completed',
    });
  }

  for (const function_call of Object.values(state.function_calls)) {
    function_call.id = outputItemId;
    if (
      state.reasoning &&
      function_call.providerData?.reasoning_content === undefined
    ) {
      function_call.providerData = {
        ...function_call.providerData,
        reasoning_content: state.reasoning,
      };
    }
    // Some providers, such as Bedrock, may send two items:
    // 1) an empty argument, and 2) the actual argument data.
    // This is a workaround for that specific behavior.
    if (function_call.arguments.startsWith('{}{')) {
      function_call.arguments = function_call.arguments.slice(2);
    }
    outputs.push(function_call);
  }

  const traceChoice = buildTraceChoice(state);
  response.choices = traceChoice ? [traceChoice] : [];
  response.usage = {
    prompt_tokens: usage?.prompt_tokens ?? 0,
    completion_tokens: usage?.completion_tokens ?? 0,
    total_tokens: usage?.total_tokens ?? 0,
    prompt_tokens_details: usage?.prompt_tokens_details,
    completion_tokens_details: usage?.completion_tokens_details,
  };

  // Compose final response
  const finalEvent: protocol.StreamEventResponseCompleted = {
    type: 'response_done',
    response: {
      id: response.id,
      usage: {
        inputTokens: usage?.prompt_tokens ?? 0,
        outputTokens: usage?.completion_tokens ?? 0,
        totalTokens: usage?.total_tokens ?? 0,
        inputTokensDetails: {
          cached_tokens: usage?.prompt_tokens_details?.cached_tokens ?? 0,
        },
        outputTokensDetails: {
          reasoning_tokens:
            (usage as any)?.completion_tokens_details?.reasoning_tokens ?? 0,
        },
      },
      output: outputs,
    },
  };

  yield finalEvent;
}

function getReasoningDelta(
  delta: ChatCompletionChunk['choices'][number]['delta'] & {
    reasoning?: unknown;
    reasoning_content?: unknown;
  },
): string | undefined {
  if (typeof delta.reasoning === 'string' && delta.reasoning) {
    return delta.reasoning;
  }
  if (typeof delta.reasoning_content === 'string' && delta.reasoning_content) {
    return delta.reasoning_content;
  }
  return undefined;
}

function openScalarPart(
  state: StreamingState,
  response: ChatCompletion,
  type: 'output_text' | 'reasoning',
  chunk: ChatCompletionChunk,
): protocol.StreamEvent[] {
  if (state.activePart?.type === type) {
    return [];
  }

  const events: protocol.StreamEvent[] = closeActivePart(state, chunk);
  const partId = createOutputPartId(response, state);
  state.activePart = { type, partId };
  events.push({
    type: 'output_part_started',
    partId,
    part: { type },
    providerData: { ...chunk },
  });
  return events;
}

function applyToolCallDeltas(
  state: StreamingState,
  response: ChatCompletion,
  toolCallDeltas: NonNullable<
    ChatCompletionChunk['choices'][number]['delta']['tool_calls']
  >,
  chunk: ChatCompletionChunk,
): protocol.StreamEvent[] {
  const events: protocol.StreamEvent[] = [];
  for (const toolCallDelta of toolCallDeltas) {
    const index = toolCallDelta.index;
    if (typeof index !== 'number') {
      throw new ModelBehaviorError(
        'OpenAI Chat Completions stream tool call delta did not include an index.',
      );
    }
    if (state.closedFunctionCallIndexes.has(index)) {
      throw new ModelBehaviorError(
        `OpenAI Chat Completions stream emitted a delta for closed tool call index ${index}.`,
      );
    }

    const functionDelta = toolCallDelta.function;
    const argumentsDelta = functionDelta?.arguments ?? '';
    let functionCall = state.function_calls[index];
    let partId = state.functionCallPartIds[index];

    if (!functionCall) {
      const callId =
        typeof toolCallDelta.id === 'string' && toolCallDelta.id.length > 0
          ? toolCallDelta.id
          : undefined;
      const name =
        typeof functionDelta?.name === 'string' && functionDelta.name.length > 0
          ? functionDelta.name
          : undefined;
      if (!callId || !name) {
        throw new ModelBehaviorError(
          `OpenAI Chat Completions stream started tool call index ${index} without id and name.`,
        );
      }

      events.push(...closeActivePart(state, chunk));
      partId = callId;
      state.functionCallPartIds[index] = partId;
      functionCall = {
        id: response.id || FAKE_ID,
        arguments: '',
        name: '',
        type: 'function_call',
        callId: '',
      };
      state.function_calls[index] = functionCall;
      state.activePart = {
        type: 'function_call',
        partId,
        functionCallIndex: index,
      };
      events.push({
        type: 'output_part_started',
        partId,
        part: { type: 'function_call', callId, name },
        providerData: { ...chunk },
      });
    } else if (
      state.activePart?.type !== 'function_call' ||
      state.activePart.functionCallIndex !== index
    ) {
      throw new ModelBehaviorError(
        `OpenAI Chat Completions stream emitted a non-contiguous delta for tool call index ${index}.`,
      );
    }

    functionCall.arguments += argumentsDelta;
    functionCall.name += functionDelta?.name || '';
    if (toolCallDelta.id && !functionCall.callId) {
      functionCall.callId = toolCallDelta.id;
    }

    if (argumentsDelta) {
      events.push({
        type: 'tool_input_delta',
        partId,
        delta: argumentsDelta,
        providerData: { ...chunk },
      });
    }
  }
  return events;
}

function closeActivePart(
  state: StreamingState,
  chunk: ChatCompletionChunk | undefined,
): protocol.StreamEventOutputPartDone[] {
  if (!state.activePart) {
    return [];
  }
  const activePart = state.activePart;
  state.activePart = null;
  if (activePart.type === 'function_call') {
    state.closedFunctionCallIndexes.add(activePart.functionCallIndex);
  }
  return [
    {
      type: 'output_part_done',
      partId: activePart.partId,
      ...(chunk ? { providerData: { ...chunk } } : {}),
    },
  ];
}

function createOutputPartId(
  response: ChatCompletion,
  state: StreamingState,
): string {
  return `${response.id || FAKE_ID}:output_part:${state.nextPartIndex++}`;
}

function hasMeaningfulToolCallDelta(
  state: StreamingState,
  toolCallDelta: NonNullable<
    ChatCompletionChunk['choices'][number]['delta']['tool_calls']
  >[number],
): boolean {
  const index = toolCallDelta.index;
  if (typeof index !== 'number') {
    return true;
  }
  if (!(index in state.function_calls)) {
    return true;
  }
  return Boolean(toolCallDelta.function?.arguments);
}

function buildTraceChoice(
  state: StreamingState,
): ChatCompletion['choices'][number] | undefined {
  const toolCalls = Object.entries(state.function_calls)
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([, functionCall]) => ({
      id: functionCall.callId,
      type: 'function' as const,
      function: {
        name: functionCall.name,
        arguments: functionCall.arguments,
      },
    }));

  const content = state.text_content?.text ?? null;
  const refusal = state.refusal_content?.refusal ?? null;

  if (content === null && refusal === null && toolCalls.length === 0) {
    return undefined;
  }

  return {
    index: 0,
    logprobs: null,
    finish_reason:
      state.finishReason ?? (toolCalls.length > 0 ? 'tool_calls' : 'stop'),
    message: {
      role: 'assistant',
      content,
      refusal,
      ...(state.reasoning ? { reasoning_content: state.reasoning } : {}),
      ...(toolCalls.length > 0 ? { tool_calls: toolCalls } : {}),
    },
  };
}
